<template>
  <div style="touch-action: none !important; height: 100vh; width: 100%; background: #313131; overflow-y: auto; padding: 20px">
    <!-- Header mit Zurück-Button -->
    <v-col cols="12" class="pb-0" style="overflow-x: auto">
      <v-card-title class="h6 pa-0">
        <v-btn class="mr-2" small fab color="white" @click="$emit('return')">
          <v-icon color="black">mdi-chevron-left</v-icon>
        </v-btn>
        Trainingsplan Übersicht
      </v-card-title>
    </v-col>

    <!-- Liste der Splits mit schöner Übersicht -->
    <v-col cols="12" class="pa-0">
      <v-list two-line>
        <v-list-item v-for="(workout, index) in form.splits.workouts" :key="index" class="pa-3 split-item mx-1">
          <v-row align="center" no-gutters style="width: 100%">
            <!-- Linke Spalte: Split-Name und Details -->
            <v-col :cols="$vuetify.breakpoint.smAndDown ? '12' : '3'" :class="{ 'mb-3': $vuetify.breakpoint.smAndDown }">
              <v-list-item-title class="white--text split-title"> Split {{ index + 1 }}: {{ workout.title }} </v-list-item-title>
              <v-list-item-subtitle class="white--text split-overview">
                Übungen: {{ workout.exercises ? workout.exercises.length : 0 }} | Sätze: {{ getTotalSets(workout) }}<br />
                Muskelgruppen:
                <span v-for="(muscle, mIndex) in getActiveMuscles(workout)" :key="mIndex">
                  {{ muscle }}<span v-if="mIndex < getActiveMuscles(workout).length - 1">, </span>
                </span>
              </v-list-item-subtitle>
            </v-col>

            <!-- Mittlere Spalte: Bild und Auswahl -->
            <v-col :cols="$vuetify.breakpoint.smAndDown ? '4' : '1'" class="ml-2" grow>
              <v-card
                class="workout-card rounded-xl pa-3"
                :style="{
                  '--card-color': workout.fixedVisualization ? workout.fixedVisualization.color : getTrainingPlanVisualization(workout.title).color,
                }">
                <v-img
                  :src="workout.fixedVisualization ? workout.fixedVisualization.img : getTrainingPlanVisualization(workout.title).img"
                  height="100%"
                  width="100%"
                  contain
                  class="d-flex justify-center align-center" />

                <!-- Auswahl-Menü zum Ändern der Visualisierung -->
                <v-menu v-model="menuOpen[index]" offset-y transition="scale-transition">
                  <template #activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" fab absolute color="white" x-small style="top: 2px; right: 2px">
                      <v-icon color="blue" small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>

                  <v-list dense>
                    <v-list-item v-for="option in visualizationOptions" :key="option.key" @click="selectVisualization(option, workout, index)">
                      <v-list-item-avatar>
                        <v-img :src="option.img" contain />
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title>{{ option.text }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-card>
            </v-col>

            <!-- Rechte Spalte: Diagramm der aktiven Muskelgruppen -->
            <v-col :cols="$vuetify.breakpoint.smAndDown ? '7' : '5'" class="d-flex justify-end ml-auto">
              <MuscleDiagram
                noTouch
                height="230px"
                :split="index"
                :hideTagWindows="true"
                background="#424242"
                :exercises="workout.exercises"
                :activeClasses="getActiveMuscles(workout)" />
            </v-col>
          </v-row>
        </v-list-item>
      </v-list>
    </v-col>

    <!-- Footer: Button zum finalen Erstellen des Trainingsplans -->
    <v-col cols="12" class="mt-5">
      <v-card-actions class="justify-center">
        <v-btn color="primary" x-large rounded @click="submitPlan"> Trainingsplan erstellen </v-btn>
      </v-card-actions>
    </v-col>
  </div>
</template>

<script>
import MuscleDiagram from "./PlanRunInteractiveHuman.vue"

export default {
  name: "WorkoutSplitOverviewAndSubmit",
  props: {
    form: { type: Object, required: true },
  },
  components: { MuscleDiagram },
  data() {
    return {
      menuOpen: this.form.splits.workouts.map(() => false),
      visualizationOptions: [
        { key: "leg", text: "Beine", img: "/img/workout/legSplit.png", color: "#1C4966" },
        { key: "chest", text: "Brust", img: "/img/workout/chestSplit.png", color: "#02734A" },
        { key: "back", text: "Rücken", img: "/img/workout/backSplit.png", color: "#F2B950" },
        { key: "arm", text: "Arm", img: "/img/workout/armSplit.png", color: "#E5735D" },
        { key: "core", text: "Cardio", img: "/img/workout/cardioSplit.png", color: "#F7B5A7" },
        { key: "upper", text: "Oberkörper", img: "/img/workout/upperBodySplit.png", color: "#A3C6C4" },
      ],
    }
  },
  methods: {
    submitPlan() {
      // Für jedes Workout eine feste Visualisierung setzen, falls noch keine ausgewählt
      this.form.splits.workouts.forEach((workout) => {
        if (!workout.fixedVisualization) {
          const { img, color } = this.getTrainingPlanVisualization(workout.title)
          this.$set(workout, "fixedVisualization", { img, color })
        }
      })
      // Danach Plan abschicken
      this.$emit("submitPlan")
    },
    selectVisualization(option, workout, index) {
      this.$set(workout, "fixedVisualization", { img: option.img, color: option.color })
      this.$set(this.menuOpen, index, false)
    },
    getTrainingPlanVisualization(title) {
      const armSplitKeywords = [
        "arm",
        "arme",
        "bizeps",
        "trizeps",
        "bizepstraining",
        "trizepstraining",
        "oberarm",
        "unterarm",
        "armtraining",
        "armübung",
        "armübungen",
        "arm-workout",
        "arm-muskulatur",
        "arms",
        "biceps",
        "triceps",
        "upper arm",
        "forearm",
        "arm training",
        "arm workout",
        "arm exercises",
        "arm muscles",
      ]
      const legSplitKeywords = [
        "bein",
        "beine",
        "beinarbeit",
        "unterkörper",
        "oberschenkel",
        "waden",
        "hamstrings",
        "quadrizeps",
        "beintraining",
        "beinübungen",
        "bein-workout",
        "leg",
        "legs",
        "leg day",
        "leg workout",
        "leg training",
        "thigh",
        "calves",
        "hamstrings",
        "quads",
        "lower body",
        "leg exercises",
      ]
      const chestSplitKeywords = [
        "brust",
        "push",
        "brusttraining",
        "brustübungen",
        "brust-workout",
        "pectoralis",
        "brustmuskulatur",
        "chest",
        "chest training",
        "chest workout",
        "pectoral",
        "pecs",
        "chest exercises",
      ]
      const backSplitKeywords = [
        "rücken",
        "pull",
        "rückentraining",
        "rückenübungen",
        "rücken-workout",
        "lats",
        "latissimus",
        "trapez",
        "trapezius",
        "back",
        "back training",
        "back workout",
        "upper back",
        "lower back",
        "lats",
        "latissimus",
        "trapezius",
        "back exercises",
      ]
      const shoulderSplitKeywords = [
        "schulter",
        "schultertraining",
        "schulterübungen",
        "schulter-workout",
        "deltoid",
        "deltoideus",
        "shoulder",
        "shoulder training",
        "shoulder workout",
        "deltoid",
        "deltoids",
        "shoulder exercises",
      ]
      const coreSplitKeywords = [
        "bauch",
        "bauchtraining",
        "bauchübungen",
        "bauch-workout",
        "sechpack",
        "magen",
        "core",
        "abs",
        "abdominals",
        "abs workout",
        "ab exercises",
        "sixpack",
        "ober",
      ]
      const lowerName = title.toLowerCase()
      if (legSplitKeywords.some((k) => lowerName.includes(k))) return this.visualizationOptions.find((o) => o.key === "leg")
      if (chestSplitKeywords.some((k) => lowerName.includes(k))) return this.visualizationOptions.find((o) => o.key === "chest")
      if (backSplitKeywords.some((k) => lowerName.includes(k))) return this.visualizationOptions.find((o) => o.key === "back")
      if (shoulderSplitKeywords.some((k) => lowerName.includes(k))) return this.visualizationOptions.find((o) => o.key === "upper")
      if (armSplitKeywords.some((k) => lowerName.includes(k))) return this.visualizationOptions.find((o) => o.key === "arm")
      if (coreSplitKeywords.some((k) => lowerName.includes(k))) return this.visualizationOptions.find((o) => o.key === "core")
      return this.visualizationOptions.find((o) => o.key === "upper") // Default to upper body if no match
    },
    getTotalSets(workout) {
      if (!workout.exercises) return 0
      return workout.exercises.reduce((sum, ex) => sum + (ex.sets?.length || 0), 0)
    },
    getActiveMuscles(workout) {
      const muscleSet = new Set()
      if (!workout.exercises) return []
      for (let exercise of workout.exercises) {
        if (exercise.tags) {
          for (let tag of exercise.tags) {
            if (tag.class) {
              muscleSet.add(tag.class)
            }
          }
        }
      }
      return Array.from(muscleSet)
    },
  },
}
</script>
<style scoped>
.white--text {
  color: white;
}
.split-item {
  background: #424242;
  border-radius: 8px;
  margin-bottom: 15px;
}
.split-title {
  font-size: 1.2rem;
  font-weight: bold;
}
.split-overview {
  font-size: 0.9rem;
  opacity: 0.9;
}

.workout-card {
  /* Der magische Teil: Erstellt den Verlauf */
  background: linear-gradient(135deg, var(--card-color), color-mix(in srgb, var(--card-color) 85%, white));

  /* Stelle sicher, dass der Text noch lesbar ist */
}

/* Optional: Ein kleiner Hover-Effekt */
</style>
